<template>
  <NodePage v-if="node" v-bind="node" :class="{ 'cursor-wait': pending }">
    <template #paragraphs-before>
      <div
        ref="scrollToEl"
        class="grid gap-30 mt-20 md:mt-40 lg:mt-60 scroll-mt-40"
        :class="{ 'opacity-25 pointer-events-none': pending }"
      >
        <EventsTeaser v-for="(event, i) in events" v-bind="event" :key="i" />
      </div>
      <div
        v-if="totalPages > 1"
        class="bg-gray-50 md:sticky md:bottom-0 mt-20 md:py-25 md:px-10 md:-mx-10"
      >
        <Pagination
          :page="page"
          :total-pages="totalPages"
          @change-page="onChangePage"
        />
      </div>
    </template>
  </NodePage>
</template>

<script lang="ts" setup>
import type { NodeEventTeaserFragment } from '#graphql-operations'
import Pagination from '~/components/Pagination/index.vue'

definePageMeta({
  name: 'pharmasuisse-events-overview',
  drupalRoute: true,
  drupalFrontendRoute: true,
  path: '/dienstleistungen/veranstaltungen',
  languageMapping: {
    fr: '/prestations/manifestations',
    it: '/servizi/eventi',
  },
})

const nuxtRoute = useRoute()

const scrollToEl = ref<HTMLDivElement | null>(null)

const { entity: node } = await useDrupalRouteQuery('nodePage')

// The amount of items per page.
const perPage = 12

// The current page for the pagination.
const page = computed<number>(() => {
  if (typeof nuxtRoute.query.page === 'string') {
    return parseInt(nuxtRoute.query.page)
  }

  return 0
})

const variables = computed<{ offset: number }>(() => {
  return {
    offset: page.value * perPage,
  }
})

// Load the articles.
const { data, pending } = await useAsyncGraphqlQuery(
  'pharmasuisseAllEvents',
  variables,
  {
    transform: (v) => v.data.upcomingEvents,
    graphqlCaching: {
      client: true,
    },
  },
)

// The events.
const events = computed<NodeEventTeaserFragment[]>(() => {
  return (data.value?.events || []).filter((v) => v && 'title' in v)
})

// The amount of total pages.
const totalPages = computed<number>(() => {
  return Math.ceil((data.value?.total || 0) / perPage)
})

const { onChangePage } = usePaginationChange(scrollToEl)

useRouteCache((c) => c.setCacheable().setMaxAge(600))
await renderPageDependencies()
</script>

<script lang="ts">
export default {
  name: 'PagePharmasuisseEvents',
}
</script>
